import { Controller } from "@hotwired/stimulus"
import { track as mixpanelTrack } from "../lib/mixpanel"

// Phase 2.2 Tier 1 — task↔line linking and settings dropdown for the
// EVA page. Mirrors budget_controller.js helpers
// (_ajaxJSON, _showToast, _show/_hide, _escape, _track, stopPropagation)
// so the two controllers use identical DOM manipulation patterns.
//
// DOM-patches chip rows directly (no full reload) so the page stays
// interactive while users link several tasks in a row.
export default class extends Controller {
  static targets = [
    "linkModal", "linkSubtitle",
    "modalLinksList", "modalLinksEmpty",
    "modalScheduleLabel",
    "taskSearch", "taskCandidates", "weightInput",
    "chipRow",
    "periodGrain", "baselineSource",
    "viewTabButton", "viewPanel",
    "asOfForm", "asOfInput",
    "captureModal", "captureLabel",
    "snapshotsModal"
  ]

  static values = {
    projectId: Number,
    budgetId: Number,
    settingsUrl: String,
    activeView: String,
    grain: String
  }

  connect() {
    this._csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    this._activeLineId = null
    this._candidates = []
    this._currentLinks = []
    this._showPendingFlash()
    if (sessionStorage.getItem("eva_open_snapshots_modal") === "1") {
      sessionStorage.removeItem("eva_open_snapshots_modal")
      this.showSnapshotsModal()
    }
  }

  // ── View tabs ──────────────────────────────────────────────────────
  // In-page swap between "By Schedule", "By Invoice", and "By Line"
  // tabs. The server rendered the active panel with data on first load;
  // switching between pre-rendered panels is an instant DOM swap. Tabs
  // that haven't been rendered yet (because they needed their own
  // calculator pass — the schedule/invoice panels) trigger a lightweight
  // server round-trip to fetch their HTML.

  switchView(event) {
    const target = event.currentTarget
    const nextView = target.dataset.viewParam
    if (!nextView || nextView === this.activeViewValue) return

    const fromView = this.activeViewValue

    this._track("EVA View Switched", { from_view: fromView, to_view: nextView })
    if (nextView === "forecast") {
      this._track("EVA Forecast Viewed", { from_view: fromView })
    }

    const panelAlreadyRendered = this._panelFor(nextView)?.children.length > 0
    if (panelAlreadyRendered) {
      this._setActiveView(nextView)
      return
    }

    this._fetchAndRenderPanel(nextView)
  }

  async _fetchAndRenderPanel(view) {
    const panel = this._panelFor(view)
    if (!panel) return

    panel.innerHTML = `<div class="bg-white border border-gray-200 rounded-lg p-6 text-center text-gray-500 text-[13px]">Loading…</div>`
    this._setActiveView(view)

    const url = new URL(window.location.href)
    url.searchParams.set("view", view)

    try {
      const resp = await fetch(url.toString(), { headers: { "Accept": "text/html" } })
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
      const html = await resp.text()
      const doc = new DOMParser().parseFromString(html, "text/html")
      const newPanel = doc.querySelector(`[data-eva-target="viewPanel"][data-view="${view}"]`)
      if (newPanel) {
        panel.innerHTML = newPanel.innerHTML
      } else {
        panel.innerHTML = `<div class="bg-white border border-gray-200 rounded-lg p-6 text-center text-red-600 text-[13px]">Unable to load ${view}.</div>`
      }
      window.history.replaceState({}, "", url.toString())
    } catch (err) {
      panel.innerHTML = `<div class="bg-white border border-gray-200 rounded-lg p-6 text-center text-red-600 text-[13px]">Failed to load: ${this._escape(err.message)}</div>`
    }
  }

  _panelFor(view) {
    return this.viewPanelTargets.find(el => el.dataset.view === view)
  }

  _setActiveView(view) {
    this.activeViewValue = view
    this.viewTabButtonTargets.forEach(btn => {
      const active = btn.dataset.view === view
      btn.classList.toggle("border-brand", active)
      btn.classList.toggle("text-gray-900", active)
      btn.classList.toggle("font-medium", active)
      btn.classList.toggle("border-transparent", !active)
      btn.classList.toggle("text-gray-500", !active)
    })
    this.viewPanelTargets.forEach(panel => {
      panel.classList.toggle("hidden", panel.dataset.view !== view)
    })
  }

  // ── As-of date picker ──────────────────────────────────────────────

  onAsOfSubmit(event) {
    const value = this.hasAsOfInputTarget ? this.asOfInputTarget.value : null
    this._track("EVA As-Of Changed", { as_of: value, view: this.activeViewValue })
    // Let the form submit to the server normally — math needs to re-run.
  }

  submitAsOf(event) {
    if (this.hasAsOfFormTarget) this.asOfFormTarget.requestSubmit()
  }

  async updateGrain() {
    const value = this.periodGrainTarget.value
    const resp = await this._patchSettings({ default_period_grain: value })
    if (!resp.ok) {
      this._showToast(resp.body?.errors?.join(", ") || "Failed to update granularity", "error")
      return
    }

    this._showToast("Granularity updated")
    this._track("EVA Settings Updated", { field: "default_period_grain", value })
    this.grainValue = value

    // Dates + Forecast both consume the bucketed series, so their
    // rendered panels are now stale. Re-fetch the active one; leave
    // other panels alone — they'll re-fetch when the user clicks
    // back into them (the panel cache clears on grain change).
    this._invalidateGrainDependentPanels()
    if (["dates", "forecast"].includes(this.activeViewValue)) {
      this._fetchAndRenderPanel(this.activeViewValue)
    }
  }

  _invalidateGrainDependentPanels() {
    ["dates", "forecast"].forEach(view => {
      const panel = this._panelFor(view)
      if (panel && view !== this.activeViewValue) panel.innerHTML = ""
    })
  }

  async updateBaseline() {
    const value = this.baselineSourceTarget.value
    const resp = await this._patchSettings({ baseline_source: value })
    if (resp.ok) {
      this._showToast("Baseline source updated")
      this._track("EVA Settings Updated", { field: "baseline_source", value })
    } else {
      this._showToast(resp.body?.errors?.join(", ") || "Failed to update baseline source", "error")
    }
  }

  async _patchSettings(attrs) {
    return this._ajaxJSON("PATCH", this.settingsUrlValue, { eva_setting: attrs })
  }

  // ── Snapshot capture ───────────────────────────────────────────────

  showCaptureModal() {
    if (!this.hasCaptureModalTarget) return
    if (this.hasCaptureLabelTarget) this.captureLabelTarget.value = ""
    this._show(this.captureModalTarget)
  }

  hideCaptureModal() {
    if (this.hasCaptureModalTarget) this._hide(this.captureModalTarget)
  }

  // ── Snapshots list modal ───────────────────────────────────────────

  showSnapshotsModal() {
    if (this.hasSnapshotsModalTarget) this._show(this.snapshotsModalTarget)
  }

  hideSnapshotsModal() {
    if (this.hasSnapshotsModalTarget) this._hide(this.snapshotsModalTarget)
  }

  async captureSnapshot() {
    const label = this.hasCaptureLabelTarget ? this.captureLabelTarget.value.trim() : ""
    const url = `/projects/${this.projectIdValue}/budgets/${this.budgetIdValue}/eva_snapshots`
    const payload = { eva_snapshot: { label } }

    const resp = await this._ajaxJSON("POST", url, payload)
    if (!resp.ok) {
      this._showToast(resp.body?.errors?.join(", ") || "Failed to capture snapshot", "error")
      return
    }

    this._track("EVA Snapshot Captured", {
      eva_snapshot_id: resp.body?.id,
      label_provided: label.length > 0
    })
    this.hideCaptureModal()
    // Reload this page so the server-rendered snapshots modal picks up
    // the new row. Flag the reload so the modal auto-opens once it
    // reconnects — gives the user immediate visual confirmation that
    // the capture landed, no navigation away from the EVA page.
    sessionStorage.setItem("eva_flash", JSON.stringify({ message: "Snapshot captured", type: "success" }))
    sessionStorage.setItem("eva_open_snapshots_modal", "1")
    window.location.reload()
  }

  // ── Link modal ─────────────────────────────────────────────────────

  async openLinkModal(event) {
    const { lineIdParam, lineNameParam } = event.currentTarget.dataset
    this._activeLineId = parseInt(lineIdParam, 10)
    this.linkSubtitleTarget.textContent = lineNameParam || ""
    this._candidates = []
    this._currentLinks = []
    this.modalLinksListTarget.querySelectorAll("[data-eva-modal-link-row]").forEach(el => el.remove())
    this.modalLinksEmptyTarget.classList.remove("hidden")
    this.taskSearchTarget.value = ""
    this.weightInputTarget.value = "1.00"
    this.taskCandidatesTarget.innerHTML = `<div class="px-3 py-2 text-[13px] text-gray-500 italic">Loading…</div>`
    this._show(this.linkModalTarget)

    const resp = await this._ajaxJSON(
      "GET",
      `/budget_line_items/${this._activeLineId}/eva_links`
    )
    if (!resp.ok) {
      this._showToast("Failed to load tasks", "error")
      return
    }

    this._candidates = resp.body.candidates || []
    this._currentLinks = resp.body.current || []
    if (this.hasModalScheduleLabelTarget) {
      this.modalScheduleLabelTarget.textContent = resp.body.schedule
        ? `Schedule: ${resp.body.schedule.name}`
        : "No schedule selected — pick one from the header dropdown."
    }

    this._renderModalLinkedList()
    this.renderTaskCandidates()
  }

  hideLinkModal() { this._hide(this.linkModalTarget) }

  stopPropagation(event) { event.stopPropagation() }

  renderTaskCandidates() {
    if (!this._candidates) return
    const filter = (this.taskSearchTarget.value || "").toLowerCase().trim()
    const linkedIds = new Set(this._currentLinks.map(l => l.task_id))

    const rows = this._candidates
      .filter(c => !linkedIds.has(c.id))
      .filter(c => !filter || (c.label || "").toLowerCase().includes(filter) ||
                   (c.sub_label || "").toLowerCase().includes(filter))
      .slice(0, 200)

    if (rows.length === 0) {
      this.taskCandidatesTarget.innerHTML =
        `<div class="px-3 py-2 text-[13px] text-gray-500 italic">No matching tasks.</div>`
      return
    }

    this.taskCandidatesTarget.innerHTML = rows.map(c => `
      <button type="button"
              data-action="click->eva#linkTask"
              data-task-id="${c.id}"
              class="w-full text-left px-3 py-1.5 hover:bg-blue-50 cursor-pointer flex items-center justify-between gap-2">
        <div class="min-w-0">
          <div class="text-[13px] text-gray-800 truncate">${this._escape(c.label)}</div>
          ${c.sub_label ? `<div class="text-[11px] text-gray-500 truncate">${this._escape(c.sub_label)}</div>` : ""}
        </div>
        <div class="text-[11px] text-gray-400 tabular-nums whitespace-nowrap">${Math.round(c.percent_done || 0)}%</div>
      </button>
    `).join("")
  }

  async linkTask(event) {
    const row = event.currentTarget
    const taskId = parseInt(row.dataset.taskId, 10)
    const weight = parseFloat(this.weightInputTarget.value || "1.0")
    if (!Number.isFinite(weight) || weight <= 0) {
      this._showToast("Weight must be greater than 0", "error")
      return
    }

    const resp = await this._ajaxJSON("POST", "/eva_links", {
      eva_link: {
        budget_line_item_id: this._activeLineId,
        task_id: taskId,
        weight
      }
    })

    if (!resp.ok) {
      this._showToast(resp.body?.errors?.join(", ") || "Failed to link task", "error")
      return
    }

    this._track("EVA Link Created", {
      budget_line_item_id: this._activeLineId,
      task_id: taskId,
      weight
    })

    this._currentLinks.push(resp.body)
    this._appendModalLinkedRow(resp.body)
    this._appendChipToLineRow(this._activeLineId, resp.body)
    this.renderTaskCandidates()
  }

  async removeLink(event) {
    const { linkIdParam } = event.currentTarget.dataset
    const id = parseInt(linkIdParam, 10)
    const resp = await this._ajaxJSON("DELETE", `/eva_links/${id}`)
    if (!resp.ok) {
      this._showToast(resp.body?.errors?.join(", ") || "Failed to unlink task", "error")
      return
    }

    this._track("EVA Link Removed", { link_id: id })

    // Remove from modal list (if open)
    const modalRow = this.modalLinksListTarget.querySelector(`[data-eva-modal-link-row][data-eva-link-id='${id}']`)
    if (modalRow) modalRow.remove()
    this._currentLinks = this._currentLinks.filter(l => l.id !== id)
    this._updateModalLinkedEmptyState()
    this.renderTaskCandidates()

    // Remove chip from the tree (wherever it lives)
    const chip = this.element.querySelector(`[data-eva-link-chip][data-eva-link-id='${id}']`)
    if (chip) {
      const row = chip.closest("[data-eva-target='chipRow']") || chip.parentElement
      chip.remove()
      this._ensureEmptyChipPlaceholder(row)
    }
  }

  // ── DOM patchers ───────────────────────────────────────────────────

  _appendChipToLineRow(lineId, link) {
    const row = this._findChipRow(lineId)
    if (!row) return
    const placeholder = row.querySelector("[data-eva-empty-chip]")
    if (placeholder) placeholder.remove()

    const chip = document.createElement("span")
    chip.dataset.evaLinkChip = ""
    chip.dataset.evaLinkId = String(link.id)
    chip.dataset.taskId = String(link.task_id)
    chip.className = "inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-blue-50 border border-blue-100 text-blue-800 text-[11px]"
    const weight = Number(link.weight ?? 1.0)
    const weightLabel = weight !== 1 ? `<span class="text-blue-500 tabular-nums">× ${weight.toFixed(2)}</span>` : ""
    chip.innerHTML = `
      <span class="truncate max-w-[14rem]" title="${this._escape(link.label)}">${this._escape(link.label)}</span>
      ${weightLabel}
      <button type="button"
              data-action="click->eva#removeLink"
              data-link-id-param="${link.id}"
              class="ml-0.5 p-0.5 rounded-full text-blue-400 hover:text-red-600 hover:bg-red-50 cursor-pointer"
              title="Unlink">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-3 h-3">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/>
        </svg>
      </button>
    `
    row.appendChild(chip)
  }

  _ensureEmptyChipPlaceholder(row) {
    if (!row) return
    const anyChip = row.querySelector("[data-eva-link-chip]")
    if (anyChip) return
    if (row.querySelector("[data-eva-empty-chip]")) return
    const empty = document.createElement("span")
    empty.dataset.evaEmptyChip = ""
    empty.className = "text-[11px] text-gray-400 italic"
    empty.textContent = "No tasks linked"
    row.appendChild(empty)
  }

  _findChipRow(lineId) {
    return this.element.querySelector(`[data-eva-target='chipRow'][data-line-id='${lineId}']`)
  }

  _renderModalLinkedList() {
    this.modalLinksListTarget.querySelectorAll("[data-eva-modal-link-row]").forEach(el => el.remove())
    this._currentLinks.forEach(l => this._appendModalLinkedRow(l))
    this._updateModalLinkedEmptyState()
  }

  _appendModalLinkedRow(link) {
    const weight = Number(link.weight ?? 1.0)
    const el = document.createElement("div")
    el.dataset.evaModalLinkRow = ""
    el.dataset.evaLinkId = String(link.id)
    el.className = "flex items-center justify-between bg-blue-50 border border-blue-100 rounded-md px-2 py-1"
    el.innerHTML = `
      <div class="min-w-0">
        <div class="text-[13px] text-gray-800 truncate">${this._escape(link.label)}</div>
        <div class="text-[11px] text-gray-500 flex items-center gap-2">
          ${link.sub_label ? `<span>${this._escape(link.sub_label)}</span>` : ""}
          <span>${Math.round(link.percent_done || 0)}% complete</span>
          ${weight !== 1 ? `<span>× ${weight.toFixed(2)} weight</span>` : ""}
        </div>
      </div>
      <button type="button"
              data-action="click->eva#removeLink"
              data-link-id-param="${link.id}"
              class="text-gray-400 hover:text-red-600 cursor-pointer ml-2 p-1"
              title="Unlink">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/>
        </svg>
      </button>
    `
    this.modalLinksListTarget.appendChild(el)
    this.modalLinksEmptyTarget.classList.add("hidden")
  }

  _updateModalLinkedEmptyState() {
    const hasAny = this.modalLinksListTarget.querySelector("[data-eva-modal-link-row]")
    if (hasAny) {
      this.modalLinksEmptyTarget.classList.add("hidden")
    } else {
      this.modalLinksEmptyTarget.classList.remove("hidden")
    }
  }

  // ── Shared helpers (parallel to budget_controller.js) ──────────────

  _escape(str) {
    return String(str ?? "").replace(/[&<>"']/g, c => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
    }[c]))
  }

  _show(el) { el.classList.remove("hidden") }
  _hide(el) { el.classList.add("hidden") }

  _flashAndReload(message, type = "success") {
    sessionStorage.setItem("eva_flash", JSON.stringify({ message, type }))
    window.location.reload()
  }

  _showPendingFlash() {
    const raw = sessionStorage.getItem("eva_flash")
    if (!raw) return
    sessionStorage.removeItem("eva_flash")
    try {
      const { message, type } = JSON.parse(raw)
      this._showToast(message, type)
    } catch { /* ignore */ }
  }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const iconSvg = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = iconSvg
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }

  async _ajaxJSON(method, url, payload) {
    const options = {
      method,
      headers: {
        "X-CSRF-Token": this._csrfToken,
        "Accept": "application/json"
      }
    }
    if (payload !== undefined && method !== "GET") {
      options.headers["Content-Type"] = "application/json"
      options.body = JSON.stringify(payload)
    }

    const resp = await fetch(url, options)
    let body = null
    try {
      if (resp.headers.get("content-type")?.includes("application/json")) {
        body = await resp.json()
      }
    } catch { /* ignore */ }
    return { ok: resp.ok, status: resp.status, body }
  }

  _track(event, props = {}) {
    try {
      mixpanelTrack(event, {
        project_id: this.projectIdValue,
        budget_id: this.budgetIdValue,
        ...props
      })
    } catch { /* mixpanel optional */ }
  }
}
